import { useState } from "react";
import { Check, Clock, Hammer, Link2, RefreshCw, Rows3, TriangleAlert } from "lucide-react";

import type { ScoutJob } from "@/lib/integrations/api";
import { ago, plainError, scoutJobStatus, scoutRunStatus } from "@/lib/integrations/words";

import { SkeletonRows } from "./Banner";
import { Button, SearchField } from "./controls";
import { TonePill } from "./HealthPill";
import styles from "./integrations.module.css";
import kit from "./kit.module.css";
import { Empty, Toolbar } from "./surfaces";

function host(url: string | null): string {
  if (!url) return "";
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * Scout's jobs as choices a person can scan: name, site, the newest passed run
 * with its rows, the last run, and the job's state. The picked one is outlined
 * and ticked.
 */
export function JobPicker({
  jobs,
  loading,
  error,
  selected,
  onPick,
  onBuild,
  onRetry,
}: {
  jobs: ScoutJob[] | undefined;
  loading: boolean;
  error?: string | null;
  selected: string | null;
  onPick: (job: ScoutJob) => void;
  onBuild: () => void;
  onRetry: () => void;
}) {
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const shown = (jobs ?? []).filter(
    (job) => !needle || job.name.toLowerCase().includes(needle) || (job.websiteUrl ?? "").toLowerCase().includes(needle),
  );
  return (
    <div className={`${styles.col} ${styles.colTight}`}>
      <Toolbar>
        <SearchField value={search} placeholder="Find a Scout job" aria-label="Find a Scout job" onChange={(event) => setSearch(event.target.value)} />
        {jobs && (
          <span className={styles.faint}>
            {shown.length === jobs.length ? `${jobs.length} jobs` : `${shown.length} of ${jobs.length} jobs`}
          </span>
        )}
        <span className={kit.spacer} />
        <Button tone="subtle" onClick={onRetry} disabled={loading}>
          <RefreshCw size={15} aria-hidden />
          Read again
        </Button>
        <Button onClick={onBuild}>
          <Hammer size={15} aria-hidden />
          Build a new job
        </Button>
      </Toolbar>
      {loading && !jobs ? (
        <SkeletonRows rows={5} columns={4} label="Reading Scout's jobs" />
      ) : error && !jobs ? null : shown.length === 0 ? (
        <Empty
          title={(jobs ?? []).length === 0 ? "Scout has no jobs yet" : "No job matches that"}
          note={(jobs ?? []).length === 0 ? "Build one — Scout's agent writes the scraper." : undefined}
          action={(jobs ?? []).length === 0 ? <Button tone="primary" onClick={onBuild}>Build a new job</Button> : undefined}
        />
      ) : (
        <ul className={styles.choices} aria-label="Scout jobs">
          {shown.map((job) => {
            const state = scoutJobStatus(job.status);
            const last = job.lastRun ? scoutRunStatus(job.lastRun.status) : null;
            const isPicked = selected === job.id;
            return (
              <li key={job.id}>
                <button
                  type="button"
                  className={isPicked ? `${styles.choice} ${styles.choiceOn}` : styles.choice}
                  aria-pressed={isPicked}
                  onClick={() => onPick(job)}
                >
                  <span className={styles.choiceHead}>
                    <span className={styles.choiceName}>{job.name}</span>
                    <span className={styles.choiceTick} aria-hidden>
                      <Check size={14} />
                    </span>
                  </span>
                  <span className={styles.choiceSite}>{host(job.websiteUrl) || "No website given"}</span>
                  <span className={styles.choiceFacts}>
                    <TonePill tone={state.tone}>{state.word}</TonePill>
                    {job.lastPassed ? (
                      <span className={styles.choiceFact}>
                        <Rows3 size={13} aria-hidden />
                        {job.lastPassed.rows.toLocaleString()} rows, passed {ago(job.lastPassed.startedAt)}
                      </span>
                    ) : (
                      <span className={`${styles.choiceFact} ${styles.warnText}`}>
                        <TriangleAlert size={13} aria-hidden />
                        No passed run yet
                      </span>
                    )}
                  </span>
                  <span className={styles.choiceFacts}>
                    <span className={styles.choiceFact} title={job.lastRun ? plainError(job.lastRun.error) || undefined : undefined}>
                      <Clock size={13} aria-hidden />
                      {job.lastRun && last ? `Last run ${last.word.toLowerCase()}, ${ago(job.lastRun.startedAt)}` : "Never run"}
                    </span>
                    {job.usedBy.length > 0 && (
                      <span className={styles.choiceFact}>
                        <Link2 size={13} aria-hidden />
                        Used by {job.usedBy.join(", ")}
                      </span>
                    )}
                  </span>
                  <span className={styles.srOnly}>{isPicked ? "Picked" : "Pick this job"}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
